// Post-auth referral claim watcher.
//
// After a user signs up via a `/auth?ref=<uuid>` link, this subscriber
// resolves the referrer from (a) supabase user_metadata (durable across
// email-confirmation round-trips) or (b) sessionStorage (survives the
// same-origin Google OAuth redirect), then calls the claim_referral RPC
// once per session. The RPC is idempotent — repeat calls for the same
// (referrer, referred) pair return `already_claimed` and are harmless.
//
// On success we fire `referral.rewarded` so analytics + toast
// subscribers can react. The server has already done the acorn award
// and audit insert; the client event is telemetry, not the source of
// truth.

import * as Sentry from '@sentry/react'
import { supabase } from './supabase'
import { fireEvent } from './events'

const REFERRAL_STORAGE_KEY = 'mori.referrer_id'
const CLAIM_ATTEMPTED_KEY = 'mori.referrer_claim_attempted'

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

type ClaimResult = {
  success: boolean
  amount_awarded?: number
  referrer_id?: string
  reason?: string
}

function readSessionReferrer(): string | null {
  try {
    const v = sessionStorage.getItem(REFERRAL_STORAGE_KEY)
    return v && UUID_REGEX.test(v) ? v : null
  } catch {
    return null
  }
}

function clearStashedReferrer(): void {
  try {
    sessionStorage.removeItem(REFERRAL_STORAGE_KEY)
    sessionStorage.setItem(CLAIM_ATTEMPTED_KEY, '1')
  } catch {
    /* storage blocked — nothing to clear */
  }
}

function alreadyAttemptedThisSession(): boolean {
  try {
    return sessionStorage.getItem(CLAIM_ATTEMPTED_KEY) === '1'
  } catch {
    return false
  }
}

export async function attemptReferralClaim(userId: string): Promise<void> {
  if (alreadyAttemptedThisSession()) return

  const { data: { user } } = await supabase.auth.getUser()
  const metadataRef = user?.user_metadata?.referrer_id as string | undefined
  const sessionRef = readSessionReferrer()
  const referrerId =
    metadataRef && UUID_REGEX.test(metadataRef)
      ? metadataRef
      : sessionRef

  if (!referrerId) return
  if (referrerId === userId) {
    clearStashedReferrer()
    return
  }

  try {
    const { data, error } = await supabase.rpc('claim_referral', {
      p_referrer_id: referrerId,
    })

    if (error) {
      Sentry.captureException(error, { tags: { flow: 'referral-claim' } })
      return
    }

    const result = data as ClaimResult | null

    // Whether it succeeded, was already claimed, or the profile aged
    // out, we don't want to retry this claim for the rest of the
    // session. The RPC is idempotent server-side regardless.
    clearStashedReferrer()

    if (result?.success && (result.amount_awarded ?? 0) > 0) {
      fireEvent('referral.rewarded', {
        userId: referrerId,
        referrerId,
        referredId: userId,
        amount: result.amount_awarded ?? 0,
        timestamp: Date.now(),
      })
    }
  } catch (err) {
    Sentry.captureException(err, { tags: { flow: 'referral-claim' } })
  }
}

export function initReferralClaim(): void {
  // Fire once for the session that's already live on app boot (covers
  // email-confirm → tab opens already signed in).
  supabase.auth.getSession().then(({ data: { session } }) => {
    if (session?.user) {
      void attemptReferralClaim(session.user.id)
    }
  })

  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN' && session?.user) {
      void attemptReferralClaim(session.user.id)
    }
  })
}
